import { useEffect } from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
  SidebarFooter,
  SidebarTrigger,
} from '@antimatterhq/ui/components/sidebar';
import { Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@antimatterhq/ui/components/switch';
import { cn } from '@antimatterhq/ui/lib/utils';
import { useColorMode } from '@antimatterhq/ui/contexts/ColorModeContext';
import { Separator } from '@antimatterhq/ui/components/separator';
import {
  setLocalStorageValue,
  prefixStorageKey,
} from '@antimatterhq/ui/utils/localStorage';
import { Icon } from '@antimatterhq/ui/components/icon';
import { StandaloneSidebarClientMenuItems } from './StandaloneSidebarClientMenuItems';
import { useInstanceId } from '@/contexts/InstanceIdContext';
import { useWorkspaceLogout } from '@/hooks/workspace/useWorkspaceLogout';
import { useConfig } from '@/contexts/ConfigContext';

const getDatabricksMenuItems = [
  {
    title: 'Catalog',
    route: '/explore/',
    icon: () => <Icon icon="lucide:shapes" data-testid="icon-catalog" />,
  },
  {
    title: 'Dashboard',
    route: '/sql/dashboards',
    icon: () => (
      <Icon icon="lucide:layout-dashboard" data-testid="icon-dashboard" />
    ),
  },
  {
    title: 'Workspace',
    route: '/browse',
    icon: () => <Icon icon="lucide:notebook" data-testid="icon-workspace" />,
  },
];

const DarkModeToggle = () => {
  const { isDarkMode, setColorMode } = useColorMode();

  const toggleDarkMode = () => {
    setColorMode(isDarkMode ? 'light' : 'dark');
  };

  return (
    <div
      className="flex items-center gap-[6px]"
      data-testid="dark-mode-toggle-container"
    >
      <Switch
        checked={isDarkMode}
        onCheckedChange={toggleDarkMode}
        className="h-6 w-12 data-[state=checked]:bg-grey-400 dark:data-[state=checked]:bg-grey-400"
        trackClassName="data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-0"
        data-testid="dark-mode-switch"
      >
        <div
          className={cn(
            'rounded-ful flex h-5 w-5 items-center justify-center',
            isDarkMode
              ? 'bg-[#111827] dark:bg-[#111827]'
              : 'bg-white dark:bg-white',
          )}
          data-testid="dark-mode-switch-thumb"
        >
          {isDarkMode ? (
            <Moon
              className="h-4 w-4 text-white dark:text-white"
              data-testid="icon-moon"
            />
          ) : (
            <Icon
              icon="antimatterhq:sun"
              className="h-4 w-4 text-yellow-400 dark:text-yellow-400"
              data-testid="icon-sun"
            />
          )}
        </div>
      </Switch>
    </div>
  );
};

export function StandaloneSidebar({ disabled }: { disabled: boolean }) {
  const { open, isMobile, setOpen } = useSidebar();
  const { isDarkMode } = useColorMode();
  const { instanceId } = useInstanceId();
  const { clearInstanceId } = useInstanceId();
  const navigate = useNavigate();
  const { IFRAME_URL } = useConfig();

  const databricksMenuItems = getDatabricksMenuItems.map(
    ({ route, ...item }) => ({
      ...item,
      url: instanceId ? `https://${instanceId}${route}` : '',
    }),
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== IFRAME_URL) {
        return;
      }

      if (event?.data?.source !== 'antimatter-client') {
        return;
      }

      if (event?.data?.sidebar_open !== undefined) {
        setOpen(event.data.sidebar_open);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [IFRAME_URL, setOpen]);

  useEffect(() => {
    setLocalStorageValue(prefixStorageKey('sidebar_open'), open);
  }, [open]);

  const { refetch: logout } = useWorkspaceLogout(instanceId ?? '');

  const handleLogout = async () => {
    await logout();
    clearInstanceId();
    navigate('/');
  };

  const renderAntimatterLogo = () => {
    return open || isMobile ? (
      <img
        src={
          isDarkMode
            ? '/images/logo/antimatter-sidebar-logo-dark.svg'
            : '/images/logo/antimatter-sidebar-logo-light.svg'
        }
        alt="logo"
        className="h-8 w-full object-contain"
        data-testid="antimatter-logo-full"
      />
    ) : (
      <img
        src={
          isDarkMode
            ? '/images/logo/antimatter-monogram-dark.svg'
            : '/images/logo/antimatter-monogram-light.svg'
        }
        alt="logo"
        className="h-8 w-8 object-contain"
        data-testid="antimatter-logo-monogram"
      />
    );
  };

  const renderDatabricksLogo = () => {
    return open || isMobile ? (
      <img
        src={
          isDarkMode
            ? '/images/databricks-logo/databricks-logo-full-dark.svg'
            : '/images/databricks-logo/databricks-logo-full-light.svg'
        }
        alt="databricks-logo"
        className="h-8 w-auto object-contain p-[6px]"
        data-testid="databricks-logo-full"
      />
    ) : (
      <img
        src={
          isDarkMode
            ? '/images/databricks-logo/databricks-monogram-dark.svg'
            : '/images/databricks-logo/databricks-monogram-light.svg'
        }
        alt="databricks-logo"
        className="h-8 w-8 object-contain p-[6px]"
        data-testid="databricks-logo-monogram"
      />
    );
  };

  return (
    <Sidebar collapsible="icon" data-testid="standalone-sidebar">
      <SidebarHeader
        className={cn(
          'relative items-start p-0 pb-0',
          !isMobile && !open && 'pl-0 pr-0',
        )}
        data-testid="sidebar-header"
      >
        <div
          className={cn('w-full overflow-hidden pl-2 pr-2')}
          data-testid="logos-container"
        >
          <div
            className="flex h-11 w-[208px] items-center"
            data-testid="databricks-logo-container"
          >
            {renderDatabricksLogo()}
          </div>
          <Separator
            orientation="horizontal"
            className="w-full bg-black dark:bg-white"
            data-testid="logos-separator"
          />
          <div
            className="flex h-11 w-[208px] items-center"
            data-testid="antimatter-logo-container"
          >
            {renderAntimatterLogo()}
          </div>
        </div>
        <SidebarTrigger
          className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-grey-200 p-0 text-black dark:bg-grey-600 dark:text-white"
          data-testid="sidebar-trigger"
        />
      </SidebarHeader>
      <SidebarContent data-testid="sidebar-content">
        <SidebarGroup data-testid="security-lakehouse-group">
          <SidebarGroupLabel data-testid="security-lakehouse-label">
            Security Lakehouse
          </SidebarGroupLabel>
          <SidebarGroupContent data-testid="security-lakehouse-content">
            <SidebarMenu data-testid="security-lakehouse-menu">
              <StandaloneSidebarClientMenuItems disabled={disabled} />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup data-testid="databricks-group">
          <SidebarGroupLabel data-testid="databricks-label">
            Databricks
          </SidebarGroupLabel>
          <SidebarGroupContent data-testid="databricks-content">
            <SidebarMenu data-testid="databricks-menu">
              {databricksMenuItems.map((item) => (
                <SidebarMenuItem
                  key={item.title}
                  data-testid={`databricks-menu-item-${item.title.toLowerCase()}`}
                >
                  <SidebarMenuButton
                    asChild
                    disabled={disabled || !item.url}
                    data-testid={`databricks-menu-button-${item.title.toLowerCase()}`}
                  >
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`databricks-link-${item.title.toLowerCase()}`}
                    >
                      <item.icon />
                      <span
                        data-testid={`databricks-text-${item.title.toLowerCase()}`}
                      >
                        {item.title}
                      </span>
                      <Icon
                        className="ml-auto"
                        icon="lucide:external-link"
                        data-testid={`external-link-${item.title.toLowerCase()}`}
                      />
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="bg-none" data-testid="sidebar-footer">
        {open ? <DarkModeToggle /> : null}
        <SidebarMenu data-testid="footer-menu">
          <SidebarMenuItem key="support" data-testid="menu-item-support">
            <SidebarMenuButton
              asChild
              isActive={false}
              data-testid="menu-button-support"
            >
              <a href="mailto:support@antimatter.io" data-testid="support-link">
                <Icon
                  icon="lucide:life-buoy"
                  className="h-4 w-4"
                  data-testid="icon-support"
                />
                <span data-testid="support-text">Support</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem key="logout" data-testid="menu-item-support">
            <SidebarMenuButton
              asChild
              isActive={false}
              data-testid="menu-button-logout"
            >
              <div data-testid="support-link" onClick={handleLogout}>
                <Icon
                  icon="lucide:log-out"
                  className="h-4 w-4"
                  data-testid="icon-logout"
                />
                <span data-testid="logout-text">Logout</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
